/**
 * AnalysisDosingChart - Displays dosing volumes as stacked bars.
 *
 * Shows daily dosing volumes for selected chemicals as stacked bars.
 * Each chemical gets its own color, and bars are stacked to show total volume.
 * Y-axis shows volume in mL, X-axis shows dates.
 */
import { useMemo } from 'react';
import { Paper, Text } from '@mantine/core';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { format, parseISO, eachDayOfInterval } from 'date-fns';
import type { DosingPoint, TaskEvent } from '@/hooks/useAnalysisData';
import { ANALYSIS_DOSING_COLORS } from '@/utils/chartColors';
import { CHART_GRID_STROKE, CHART_AXIS_TICK, CHART_LEGEND_STYLE } from '@/utils/chartStyles';
import { AnalysisEventLines } from './AnalysisEventLines';
import {
  TooltipContainer,
  TooltipHeader,
  TooltipRow,
  TooltipFooter,
} from './components/ChartTooltip';

/** Height of the dosing chart in pixels */
const CHART_HEIGHT = 200;

interface AnalysisDosingChartProps {
  /** List of selected dosing chemical names to display */
  selectedDosing: string[];
  /** Dosing data grouped by chemical name */
  dosing: Record<string, DosingPoint[]>;
  /** Time domain for X-axis alignment with other charts */
  timeDomain?: { startTs: number; endTs: number };
  /** List of selected event type names to display */
  selectedEvents?: string[];
  /** All available maintenance events */
  events?: TaskEvent[];
  /** Date range for event positioning (YYYY-MM-DD format) */
  dateRange?: { start: string; end: string };
  /** Whether to show vertical event lines on the chart */
  showEventLines?: boolean;
}

interface ChartDataPoint {
  date: string;
  timestamp: number;
  displayDate: string;
  [chemical: string]: string | number; // volume values keyed by chemical name
}

/**
 * Transform dosing data into stacked bar chart format.
 * Groups data by date and fills every day in the range so bars are each one day wide.
 */
function transformToChartData(
  selectedDosing: string[],
  dosing: Record<string, DosingPoint[]>,
  timeDomain?: { startTs: number; endTs: number }
): ChartDataPoint[] {
  // Collect dosing data keyed by date
  const dateMap = new Map<string, ChartDataPoint>();

  selectedDosing.forEach((chemical) => {
    const points = dosing[chemical] || [];

    points.forEach((point) => {
      if (!dateMap.has(point.date)) {
        dateMap.set(point.date, {
          date: point.date,
          timestamp: new Date(`${point.date}T12:00:00`).getTime(),
          displayDate: format(parseISO(point.date), 'MMM d'),
        });
      }

      const dataPoint = dateMap.get(point.date)!;
      dataPoint[chemical] = point.volume_ml;
    });
  });

  if (dateMap.size === 0) return [];

  // Fill in every day in the range so bars are each exactly one day wide
  const start = timeDomain
    ? new Date(timeDomain.startTs)
    : new Date(Math.min(...Array.from(dateMap.values()).map((d) => d.timestamp)));
  const end = timeDomain
    ? new Date(timeDomain.endTs)
    : new Date(Math.max(...Array.from(dateMap.values()).map((d) => d.timestamp)));

  return eachDayOfInterval({ start, end }).map((day) => {
    const dateStr = format(day, 'yyyy-MM-dd');
    return dateMap.get(dateStr) ?? {
      date: dateStr,
      timestamp: new Date(`${dateStr}T12:00:00`).getTime(),
      displayDate: format(day, 'MMM d'),
    };
  });
}

/**
 * Filter to only include chemicals that have data.
 */
function filterChemicalsWithData(
  selectedDosing: string[],
  dosing: Record<string, DosingPoint[]>
): string[] {
  return selectedDosing.filter((chemical) => {
    const points = dosing[chemical];
    return points && points.length > 0;
  });
}

/**
 * Custom tooltip formatter for dosing chart.
 */
function CustomTooltip({
  active,
  payload,
}: {
  active?: boolean;
  payload?: Array<{ name: string; value: number; fill: string; payload?: ChartDataPoint }>;
  label?: string;
}): JSX.Element | null {
  if (!active || !payload || payload.length === 0) {
    return null;
  }

  // Get the date from the first payload item
  const dateString = payload[0]?.payload?.date;

  // Calculate total volume
  const total = payload.reduce((sum, entry) => sum + (entry.value || 0), 0);

  return (
    <TooltipContainer>
      {dateString && (
        <TooltipHeader
          timestamp={`${dateString}T12:00:00`}
          showTime={false}
        />
      )}
      {payload.map((entry) => (
        <TooltipRow
          key={entry.name}
          color={entry.fill}
          label={entry.name}
          value={(entry.value ?? 0).toFixed(1)}
          unit="mL"
        />
      ))}
      {payload.length > 1 && (
        <TooltipFooter>
          Total: {total.toFixed(1)} mL
        </TooltipFooter>
      )}
    </TooltipContainer>
  );
}

/**
 * Stacked bar chart showing dosing volumes for selected chemicals.
 * Returns null if no dosing items are selected or no data is available.
 */
export function AnalysisDosingChart({
  selectedDosing,
  dosing,
  timeDomain,
  selectedEvents = [],
  events = [],
  dateRange,
  showEventLines = false,
}: AnalysisDosingChartProps): JSX.Element | null {
  // Filter to chemicals that have data
  const chemicalsWithData = useMemo(
    () => filterChemicalsWithData(selectedDosing, dosing),
    [selectedDosing, dosing]
  );

  // Transform data for the chart
  const chartData = useMemo(
    () => transformToChartData(chemicalsWithData, dosing, timeDomain),
    [chemicalsWithData, dosing, timeDomain]
  );

  // Return null if no dosing items selected or no data available
  if (selectedDosing.length === 0 || chemicalsWithData.length === 0) {
    return null;
  }

  return (
    <Paper p="md" withBorder radius="md">
      <Text fw={600} size="sm" mb="sm">
        Dosing Volumes
      </Text>
      <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
        <BarChart data={chartData} margin={{ top: 10, right: 30, left: 10, bottom: 10 }}>
          <CartesianGrid strokeDasharray="3 3" stroke={CHART_GRID_STROKE} vertical={false} />
          <XAxis
            dataKey="timestamp"
            type="number"
            domain={timeDomain ? [timeDomain.startTs, timeDomain.endTs] : ['dataMin', 'dataMax']}
            tickFormatter={(value) => format(new Date(value), 'MMM d')}
            tick={CHART_AXIS_TICK}
            axisLine={false}
            tickLine={false}
          />
          <YAxis
            label={{ value: 'Volume (mL)', angle: -90, position: 'insideLeft', style: { fontSize: 11 } }}
            tick={CHART_AXIS_TICK}
            width={50}
            axisLine={false}
            tickLine={false}
          />
          <Tooltip
            content={<CustomTooltip />}
            cursor={{ fill: 'rgba(148, 163, 184, 0.1)' }}
          />
          <Legend
            verticalAlign="bottom"
            height={36}
            wrapperStyle={CHART_LEGEND_STYLE}
          />
          {chemicalsWithData.map((chemical, index) => (
            <Bar
              key={chemical}
              dataKey={chemical}
              name={chemical}
              fill={ANALYSIS_DOSING_COLORS[index % ANALYSIS_DOSING_COLORS.length]}
              stackId="dosing"
            />
          ))}
          {/* Event lines for timing correlation */}
          {dateRange && (
            <AnalysisEventLines
              selectedEvents={selectedEvents}
              events={events}
              dateRange={dateRange}
              showEventLines={showEventLines}
            />
          )}
        </BarChart>
      </ResponsiveContainer>
    </Paper>
  );
}
